import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TtsProvider } from '../hooks/useTextToSpeech';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { useVoicePrefs } from '../lib/queries/useVoicePrefs';
import './VoicePicker.css';

/** Cloud (OpenAI tts-1-hd) voice options with short descriptors so the
 *  coach knows what they're picking. "fable" leads — it's the British-
 *  leaning male voice we default to. */
const CLOUD_VOICES: Array<{ id: string; label: string; description: string; gender: 'male' | 'female' | 'neutral' }> = [
  { id: 'fable',   label: 'Fable',   description: 'British-leaning male, warm storyteller (recommended)', gender: 'male' },
  { id: 'echo',    label: 'Echo',    description: 'Warm male, conversational',                              gender: 'male' },
  { id: 'ash',     label: 'Ash',     description: 'Confident male, plain-speaking',                         gender: 'male' },
  { id: 'onyx',    label: 'Onyx',    description: 'Deep male, gravitas',                                    gender: 'male' },
  { id: 'ballad',  label: 'Ballad',  description: 'Emotive male, expressive',                               gender: 'male' },
  { id: 'sage',    label: 'Sage',    description: 'Measured female, thoughtful',                            gender: 'female' },
  { id: 'coral',   label: 'Coral',   description: 'Warm female, conversational',                            gender: 'female' },
  { id: 'shimmer', label: 'Shimmer', description: 'Bright female, energetic',                               gender: 'female' },
  { id: 'nova',    label: 'Nova',    description: 'Smooth female, calm',                                    gender: 'female' },
  { id: 'verse',   label: 'Verse',   description: 'Animated, expressive',                                   gender: 'neutral' },
  { id: 'alloy',   label: 'Alloy',   description: 'Balanced neutral',                                       gender: 'neutral' },
];

/** ElevenLabs Turbo v2.5 voices — premium quality. Keys are the friendly
 *  names the edge function recognises; the IDs live server-side. Daniel
 *  leads — confident UK male, the closest match to Brain's senior-coach
 *  character. */
const ELEVEN_VOICES: Array<{ id: string; label: string; description: string; gender: 'male' | 'female' | 'neutral' }> = [
  { id: 'daniel',    label: 'Daniel',    description: 'UK male, confident senior coach (recommended)',     gender: 'male' },
  { id: 'george',    label: 'George',    description: 'UK male, warmer middle-aged register',              gender: 'male' },
  { id: 'charlotte', label: 'Charlotte', description: 'UK female, warm',                                   gender: 'female' },
  { id: 'lily',      label: 'Lily',      description: 'UK female, calmer / lower register',                gender: 'female' },
];

const TEST_LINE = "Right, that's me — give us a shout if this lands well for you.";

interface VoicePickerProps {
  onClose: () => void;
  /** Optional element the picker should anchor under. When provided, the
   *  picker is portaled to document.body and positioned via
   *  getBoundingClientRect — bypasses the brain panel's overflow:hidden
   *  clip. When omitted, falls back to inline absolute positioning. */
  anchor?: HTMLElement | null;
}

export function VoicePicker({ onClose, anchor }: VoicePickerProps): React.JSX.Element {
  const { prefs, save } = useVoicePrefs();
  const tts = useTextToSpeech({
    provider: prefs.voice_provider,
    rate: prefs.voice_rate,
    voiceId: prefs.voice_id,
  });
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female'>('all');
  const panelRef = useRef<HTMLDivElement>(null);
  const [portalStyle, setPortalStyle] = useState<{ top: number; right: number } | null>(null);

  // When portaled (anchor provided) compute fixed-position coords from the
  // trigger button's bounding rect. Recompute on resize / scroll so the
  // picker tracks the anchor if the page reflows behind it.
  useLayoutEffect(() => {
    if (!anchor) {
      setPortalStyle(null);
      return;
    }
    function compute(): void {
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      setPortalStyle({
        top: r.bottom + 8,
        right: window.innerWidth - r.right,
      });
    }
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [anchor]);

  // Close on outside click / escape. When portaled, also ignore clicks on
  // the anchor button itself — otherwise toggling closes immediately.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (!panelRef.current) return;
      const target = e.target as Node;
      if (panelRef.current.contains(target)) return;
      if (anchor && anchor.contains(target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [onClose, anchor]);

  const cloudVoices = useMemo(
    () => CLOUD_VOICES.filter((v) => genderFilter === 'all' || v.gender === genderFilter),
    [genderFilter],
  );
  const elevenVoices = useMemo(
    () => ELEVEN_VOICES.filter((v) => genderFilter === 'all' || v.gender === genderFilter),
    [genderFilter],
  );

  // System voices — group British accents first.
  const systemRanked = useMemo(() => {
    const list = tts.systemVoices;
    const score = (v: SpeechSynthesisVoice): number => {
      // Best first: UK English voices > other English > everything else.
      if (/en[-_]gb/i.test(v.lang)) return 0;
      if (/google uk english/i.test(v.name)) return 1;
      if (/^en/i.test(v.lang)) return 2;
      return 3;
    };
    return [...list].sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name));
  }, [tts.systemVoices]);

  function setProvider(provider: TtsProvider): void {
    // Reset voice_id when swapping provider so we land on each provider's sensible default.
    if (provider === 'cloud') {
      void save({
        voice_provider: 'cloud',
        voice_id: prefs.voice_id && CLOUD_VOICES.some((v) => v.id === prefs.voice_id) ? prefs.voice_id : 'fable',
      });
    } else if (provider === 'eleven') {
      void save({
        voice_provider: 'eleven',
        voice_id: prefs.voice_id && ELEVEN_VOICES.some((v) => v.id === prefs.voice_id) ? prefs.voice_id : 'daniel',
      });
    } else {
      void save({ voice_provider: 'system', voice_id: null });
    }
  }

  function setCloudVoice(id: string): void {
    void save({ voice_provider: 'cloud', voice_id: id });
  }

  function setElevenVoice(id: string): void {
    void save({ voice_provider: 'eleven', voice_id: id });
  }

  function setSystemVoice(uri: string): void {
    void save({ voice_provider: 'system', voice_id: uri });
  }

  function setEnabled(enabled: boolean): void {
    void save({ voice_enabled: enabled });
    if (!enabled) tts.stop();
  }

  function setRate(rate: number): void {
    void save({ voice_rate: rate });
  }

  const [testStatus, setTestStatus] = useState<string | null>(null);
  function testVoice(): void {
    tts.stop();
    // Diagnose what's actually happening — sync to status banner + console.
    if (prefs.voice_provider === 'system' && typeof window !== 'undefined' && window.speechSynthesis) {
      const all = window.speechSynthesis.getVoices();
      const match = prefs.voice_id ? all.find((v) => v.voiceURI === prefs.voice_id) : null;
      const status = match
        ? `Speaking with ${match.name} (${match.lang})`
        : prefs.voice_id
          ? `Voice "${prefs.voice_id}" not found in ${all.length} loaded voices — falling back to default`
          : `No voice picked — using browser default`;
      setTestStatus(status);
      // eslint-disable-next-line no-console
      console.log('[voice-picker] test', { provider: prefs.voice_provider, voice_id: prefs.voice_id, matched: match?.name, total_voices: all.length });
    } else if (prefs.voice_provider === 'cloud') {
      setTestStatus(`Cloud TTS · ${prefs.voice_id ?? 'fable'} (~$0.003/test)`);
    } else if (prefs.voice_provider === 'eleven') {
      setTestStatus(`ElevenLabs · ${prefs.voice_id ?? 'daniel'} (~$0.02/test)`);
    }
    tts.speak(TEST_LINE);
  }

  const panel = (
    <div
      className="voice-picker"
      ref={panelRef}
      role="dialog"
      aria-label="Brain voice settings"
      data-portaled={portalStyle ? '' : undefined}
      style={portalStyle ? { top: portalStyle.top, right: portalStyle.right } : undefined}
    >
      <header className="voice-picker__head">
        <span className="voice-picker__eyebrow">Brain voice</span>
        <button type="button" className="voice-picker__close" onClick={onClose} aria-label="Close">×</button>
      </header>

      <div className="voice-picker__row">
        <span className="voice-picker__label">Output</span>
        <div className="voice-picker__seg" role="radiogroup" aria-label="Voice output on/off">
          <button
            type="button"
            role="radio"
            aria-checked={prefs.voice_enabled}
            className="voice-picker__chip"
            data-active={prefs.voice_enabled || undefined}
            onClick={() => setEnabled(true)}
          >
            On
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!prefs.voice_enabled}
            className="voice-picker__chip"
            data-active={!prefs.voice_enabled || undefined}
            onClick={() => setEnabled(false)}
          >
            Mute
          </button>
        </div>
      </div>

      <div className="voice-picker__row">
        <span className="voice-picker__label">Source</span>
        <div className="voice-picker__seg" role="radiogroup" aria-label="Voice source">
          <button
            type="button"
            role="radio"
            aria-checked={prefs.voice_provider === 'eleven'}
            className="voice-picker__chip"
            data-active={prefs.voice_provider === 'eleven' || undefined}
            onClick={() => setProvider('eleven')}
            title="ElevenLabs Turbo v2.5 — premium, ~$0.02 per reply"
          >
            ElevenLabs ★
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={prefs.voice_provider === 'cloud'}
            className="voice-picker__chip"
            data-active={prefs.voice_provider === 'cloud' || undefined}
            onClick={() => setProvider('cloud')}
            title="OpenAI cloud TTS — solid quality, ~$0.003 per reply"
          >
            Cloud
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={prefs.voice_provider === 'system'}
            className="voice-picker__chip"
            data-active={prefs.voice_provider === 'system' || undefined}
            onClick={() => setProvider('system')}
            title="Browser / OS speech synthesis — free, varies by OS"
          >
            Browser
          </button>
        </div>
      </div>

      {prefs.voice_provider === 'eleven' && (
        <div className="voice-picker__cloud">
          <div className="voice-picker__row">
            <span className="voice-picker__label">Filter</span>
            <div className="voice-picker__seg">
              {(['all', 'male', 'female'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  className="voice-picker__chip"
                  data-active={genderFilter === g || undefined}
                  onClick={() => setGenderFilter(g)}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <ul className="voice-picker__list">
            {elevenVoices.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  className="voice-picker__voice"
                  data-active={prefs.voice_id === v.id || undefined}
                  onClick={() => setElevenVoice(v.id)}
                >
                  <span className="voice-picker__voice-name">{v.label}</span>
                  <span className="voice-picker__voice-desc">{v.description}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {prefs.voice_provider === 'cloud' && (
        <div className="voice-picker__cloud">
          <div className="voice-picker__row">
            <span className="voice-picker__label">Filter</span>
            <div className="voice-picker__seg">
              {(['all', 'male', 'female'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  className="voice-picker__chip"
                  data-active={genderFilter === g || undefined}
                  onClick={() => setGenderFilter(g)}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <ul className="voice-picker__list">
            {cloudVoices.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  className="voice-picker__voice"
                  data-active={prefs.voice_id === v.id || undefined}
                  onClick={() => setCloudVoice(v.id)}
                >
                  <span className="voice-picker__voice-name">{v.label}</span>
                  <span className="voice-picker__voice-desc">{v.description}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {prefs.voice_provider === 'system' && (
        <div className="voice-picker__system">
          {systemRanked.length === 0 ? (
            <p className="voice-picker__empty">
              No system voices detected. Try the Cloud (premium) source instead.
            </p>
          ) : (
            <ul className="voice-picker__list">
              {systemRanked.map((v) => (
                <li key={v.voiceURI}>
                  <button
                    type="button"
                    className="voice-picker__voice"
                    data-active={prefs.voice_id === v.voiceURI || undefined}
                    onClick={() => setSystemVoice(v.voiceURI)}
                  >
                    <span className="voice-picker__voice-name">{v.name}</span>
                    <span className="voice-picker__voice-desc">{v.lang}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="voice-picker__row">
        <span className="voice-picker__label">Speed</span>
        <input
          type="range"
          min={0.75}
          max={1.35}
          step={0.05}
          value={prefs.voice_rate}
          onChange={(e) => setRate(Number(e.target.value))}
          aria-label="Voice rate"
        />
        <span className="voice-picker__rate">{prefs.voice_rate.toFixed(2)}×</span>
      </div>

      <button type="button" className="voice-picker__test" onClick={testVoice}>
        ▶ Test voice
      </button>
      {testStatus && (
        <p className="voice-picker__test-status" aria-live="polite">{testStatus}</p>
      )}
      <p className="voice-picker__current" aria-live="polite">
        Active: <strong>{prefs.voice_provider}</strong>
        {prefs.voice_id ? <> · <strong>{prefs.voice_id}</strong></> : null}
        {prefs.voice_enabled ? null : <> · <strong>muted</strong></>}
      </p>
    </div>
  );
  // Portal to body when we have an anchor — escapes the brain panel's
  // overflow:hidden clip + isolation stacking context.
  if (portalStyle && typeof document !== 'undefined') {
    return createPortal(panel, document.body);
  }
  return panel;
}
